// src/pages/business/RecallControlPage.tsx - BUSINESS USERS ONLY
import React, { useState, useEffect, useCallback } from 'react';
import BusinessApiService from '../../services/BusinessApiService';
import { getCurrentBusinessId } from '../../services/api';
import { ShieldAlert, AlertTriangle, Package, Calendar, RefreshCw, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';

/* =============================================================================
   Configuration & Helpers
   ========================================================================== */
const API_BASE = process.env.REACT_APP_API_URL || "";

const authToken = () => BusinessApiService.getBusinessToken() || '';

// getCurrentBusinessId imported from ../../services/api

async function apiFetch(path: string, opts: RequestInit = {}) {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(opts.headers as any),
  };
  const token = authToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  
  const res = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  const json = await res.json().catch(() => ({}));
  
  if (!res.ok) {
    throw new Error(json?.error || json?.message || `HTTP ${res.status}`);
  }
  return json;
}

/* =============================================================================
   Types
   ========================================================================== */
interface Batch {
  batchId: string;
  name: string;
  batchNumber?: string;
  quantity: number;
  status: string;
  productId?: string;
  assignedStoreId?: string;
  recall?: {
    isRecalled?: boolean;
    reason?: string;
    severity?: 'low' | 'medium' | 'high' | 'critical';
    recalledAt?: string;
  };
  createdAt?: string;
}

/* =============================================================================
   Main Component
   ========================================================================== */
const RecallControlPage: React.FC = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const bizId = getCurrentBusinessId();
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  
  // Recall form state
  const [batchId, setBatchId] = useState(urlParams.get('batchId') || '');
  const [batchName, setBatchName] = useState(urlParams.get('batchName') || '');
  const [reason, setReason] = useState('');
  const [severity, setSeverity] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  
  // Batches list
  const [batches, setBatches] = useState<Batch[]>([]);
  const [recalledBatches, setRecalledBatches] = useState<Batch[]>([]);
  
  // Load all batches
  const loadBatches = useCallback(async () => {
    try {
      const response = await apiFetch(`/batches?businessId=${bizId}`);
      
      if (response?.success) {
        const allBatches = response.data?.batches || [];
        setBatches(allBatches);
        setRecalledBatches(allBatches.filter((b: Batch) => b.recall?.isRecalled));
      }
    } catch (err) {
      console.error('Failed to load batches:', err);
    }
  }, [bizId]);
  
  useEffect(() => {
    loadBatches();
  }, [loadBatches]);
  
  // Handle batch recall
  const handleRecall = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!batchId.trim()) {
      setMessage({ type: 'error', text: 'Please enter a Batch ID' });
      return;
    }
    
    if (!reason.trim() || reason.trim().length < 5) {
      setMessage({ type: 'error', text: 'Please enter a recall reason (minimum 5 characters)' });
      return;
    }
    
    if (!window.confirm(`Are you sure you want to recall batch ${batchId}?\n\nReason: ${reason}\nSeverity: ${severity}\n\nThis action cannot be undone.`)) {
      return;
    }
    
    try {
      setLoading(true);
      setMessage(null);
      
      const response = await apiFetch(
        `/batches/${encodeURIComponent(batchId)}/recall?businessId=${bizId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ reason, severity })
        }
      );
      
      if (response.success) {
        setMessage({ 
          type: 'success', 
          text: `Batch ${batchId} recalled successfully!` 
        });
        
        // Reset form
        setBatchId('');
        setBatchName('');
        setReason('');
        setSeverity('medium');
        
        // Reload batches
        await loadBatches();
      } else {
        throw new Error(response.error || 'Recall failed');
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to recall batch' });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => window.history.back()}
              className="p-2 hover:bg-gray-100 rounded-lg"
              title="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldAlert className="w-8 h-8 text-red-600" />
              Batch Recall Management
            </h1>
          </div>
          <p className="text-gray-600 ml-14">Recall batches for quality or safety issues</p>
        </div>
        <button
          onClick={loadBatches}
          className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>
      
      {/* Message Banner */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' && <CheckCircle className="w-5 h-5" />}
            {message.type === 'error' && <XCircle className="w-5 h-5" />}
            <span>{message.text}</span>
          </div>
        </div>
      )}
      
      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-blue-600">{batches.length}</div>
              <div className="text-sm text-gray-600">Total Batches</div>
            </div>
            <Package className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-red-600">{recalledBatches.length}</div>
              <div className="text-sm text-gray-600">Recalled Batches</div>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-green-600">
                {batches.length - recalledBatches.length}
              </div>
              <div className="text-sm text-gray-600">Active Batches</div>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
      </div>
      
      {/* Recall Form */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Recall a Batch</h3>
        </div>
        
        <div className="p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <strong>Warning:</strong> Recalling a batch will mark it as recalled and update its status. 
                This action cannot be undone. Please ensure you have the correct batch ID and a valid reason.
              </div>
            </div>
          </div>
          
          <form onSubmit={handleRecall} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Batch ID *
                </label>
                <input
                  type="text"
                  value={batchId}
                  onChange={(e) => setBatchId(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Enter batch ID (e.g., BATCH-20250104-ABC1)"
                  required
                />
                {batchName && (
                  <p className="mt-1 text-xs text-gray-500">Batch: {batchName}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Severity Level *
                </label>
                <select
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as any)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                >
                  <option value="low">Low - Minor quality issue</option>
                  <option value="medium">Medium - Moderate concern</option>
                  <option value="high">High - Serious safety risk</option>
                  <option value="critical">Critical - Immediate danger</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recall Reason * (minimum 5 characters)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows={4}
                placeholder="Describe the reason for recalling this batch (e.g., quality defect discovered, contamination risk, packaging error, safety concern)"
                required
                minLength={5}
              />
              <p className="mt-1 text-xs text-gray-500">
                {reason.length}/1000 characters
              </p>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  setBatchId('');
                  setBatchName('');
                  setReason('');
                  setSeverity('medium');
                  setMessage(null);
                }}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Clear Form
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                <ShieldAlert className="w-4 h-4" />
                {loading ? 'Recalling...' : 'Recall Batch'}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Recalled Batches List */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Recalled Batches ({recalledBatches.length})</h3>
        </div>
        
        <div className="p-6">
          {recalledBatches.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ShieldAlert className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="font-medium">No recalled batches</p>
              <p className="text-sm">All your batches are currently active</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recalledBatches.map((batch) => (
                <div key={batch.batchId} className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <ShieldAlert className="w-5 h-5 text-red-600" />
                        <h4 className="font-semibold text-gray-900">{batch.name}</h4>
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full font-medium">
                          RECALLED
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600 font-medium">Batch ID:</span>{' '}
                          <span className="font-mono text-gray-900">{batch.batchId}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 font-medium">Quantity:</span>{' '}
                          <span className="text-gray-900">{batch.quantity} units</span>
                        </div>
                        
                        {batch.recall?.reason && (
                          <div className="col-span-2">
                            <span className="text-gray-600 font-medium">Reason:</span>{' '}
                            <span className="text-gray-900">{batch.recall.reason}</span>
                          </div>
                        )}
                        
                        {batch.recall?.severity && (
                          <div>
                            <span className="text-gray-600 font-medium">Severity:</span>{' '}
                            <span className="uppercase text-red-700 font-medium">
                              {batch.recall.severity}
                            </span>
                          </div>
                        )}
                        
                        {batch.recall?.recalledAt && (
                          <div>
                            <span className="text-gray-600 font-medium">Recalled:</span>{' '}
                            <span className="text-gray-900">
                              {new Date(batch.recall.recalledAt).toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">Need Help?</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Make sure you have the correct Batch ID before recalling</li>
          <li>• Provide a clear and detailed reason for the recall</li>
          <li>• Choose the appropriate severity level based on the risk</li>
          <li>• Recalled batches cannot be unreversed - contact support if needed</li>
        </ul>
      </div>
    </div>
  );
};

export default RecallControlPage;